import type {
  DecidePendingOperationRequest,
  OperationLimitRule,
  OperationLimitRuleDeleteResponse,
  OperationLimitRuleListParams,
  OperationLimitSettings,
  PendingOperation,
  PendingOperationListParams,
  PendingOperationListResponse,
  UpdateOperationLimitSettingsRequest,
  UpsertOperationLimitRuleRequest,
} from '../types';
import type { RequestExtras, RequestFn } from '../client';

/**
 * Per-operation spending limits: rules scoped to the merchant, a role or a
 * user, the merchant-level enforcement settings, and the approvals inbox.
 * Enforcement resolves the most specific rule: user > role > merchant.
 *
 * A rule set to `require_approval` does not refuse an over-limit operation —
 * it parks it. The original call fails with HTTP 409 `DE_06` carrying
 * `PendingApprovalErrorDetails`, and the operation runs only once a second
 * person approves the request through this inbox.
 *
 * **Refunds only.** Approval needs an executor that can run the operation
 * after the decision, and only refunds have one; the settlement operations
 * take `block` alone, which the request type enforces. So every request in
 * this inbox is a refund, and `DE_06` never comes back from a settlement
 * call — an over-limit settlement adjustment or payout fails with `DE_01`.
 */
export class OperationLimits {
  constructor(private readonly request: RequestFn) {}

  /**
   * List the merchant's limit rules, optionally for one operation.
   *
   * `GET /operation-limits/rules`
   */
  async listRules(
    params?: OperationLimitRuleListParams,
    options?: RequestExtras,
  ): Promise<OperationLimitRule[]> {
    return this.request('GET', '/operation-limits/rules', {
      query: { operation: params?.operation },
      ...options,
    });
  }

  /**
   * Create or replace the limit rule for one target. Full-replace upsert:
   * absent limit fields clear that dimension.
   *
   * `PUT /operation-limits/rules`
   */
  async upsertRule(
    params: UpsertOperationLimitRuleRequest,
    options?: RequestExtras,
  ): Promise<OperationLimitRule> {
    return this.request('PUT', '/operation-limits/rules', { body: params, ...options });
  }

  /**
   * Delete a limit rule.
   *
   * `DELETE /operation-limits/rules/{ruleId}`
   */
  async deleteRule(
    ruleId: string,
    options?: RequestExtras,
  ): Promise<OperationLimitRuleDeleteResponse> {
    return this.request('DELETE', `/operation-limits/rules/${encodeURIComponent(ruleId)}`, options);
  }

  /**
   * The merchant-level enforcement settings. An untouched merchant gets the
   * defaults: rolling window, admins not exempt.
   *
   * `GET /operation-limits/settings`
   */
  async retrieveSettings(options?: RequestExtras): Promise<OperationLimitSettings> {
    return this.request('GET', '/operation-limits/settings', options);
  }

  /**
   * Update the enforcement settings. Only provided fields change.
   *
   * `PUT /operation-limits/settings`
   */
  async updateSettings(
    params: UpdateOperationLimitSettingsRequest,
    options?: RequestExtras,
  ): Promise<OperationLimitSettings> {
    return this.request('PUT', '/operation-limits/settings', { body: params, ...options });
  }

  /**
   * The approvals inbox: over-limit operations waiting on a second person.
   *
   * Both filters default rather than widen. With no `status` the list holds
   * **pending requests only** — approved, rejected and expired ones are
   * reachable only by asking for that status, so a history view must pass one
   * per status. With no `operation` it lists **refunds only**; the list is one
   * operation at a time. `limit` defaults to 100 and is clamped to 1–500.
   *
   * Requests past their `expires_at` are expired before the list is read, so
   * nothing here is shown as actionable when it is not.
   *
   * `GET /operation-limits/approvals`
   */
  async listApprovals(
    params?: PendingOperationListParams,
    options?: RequestExtras,
  ): Promise<PendingOperationListResponse> {
    return this.request('GET', '/operation-limits/approvals', {
      query: {
        operation: params?.operation,
        status: params?.status,
        limit: params?.limit,
      },
      ...options,
    });
  }

  /**
   * Approve a parked operation and execute it.
   *
   * Refused for the user who requested it, and for an approver whose own
   * limit would not have covered the operation — the permission is necessary
   * and not sufficient.
   *
   * Approval and execution are two facts. A request that was approved but
   * whose operation then failed comes back `approved` with `execution_error`
   * set and no `result_entity_id`; that is a real outcome, not a partial read.
   *
   * `POST /operation-limits/approvals/{id}/approve`
   */
  async approve(
    id: string,
    params: DecidePendingOperationRequest = {},
    options?: RequestExtras,
  ): Promise<PendingOperation> {
    return this.request('POST', `/operation-limits/approvals/${encodeURIComponent(id)}/approve`, {
      body: params,
      ...options,
    });
  }

  /**
   * Reject a parked operation. Nothing is executed and the request is closed.
   *
   * `POST /operation-limits/approvals/{id}/reject`
   */
  async reject(
    id: string,
    params: DecidePendingOperationRequest = {},
    options?: RequestExtras,
  ): Promise<PendingOperation> {
    return this.request('POST', `/operation-limits/approvals/${encodeURIComponent(id)}/reject`, {
      body: params,
      ...options,
    });
  }
}
